import {HttpErrorResponse} from '@angular/common/http';
import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {combineLatest, Observable, ReplaySubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {ApiId, TtdPriceModel} from '../../../../distribution-account/distribution-account.model';
import {TargetService, TtdConfig, TtdGroup, TtdPartner} from '../../../../target/target.service';
import {SubscriptionHandler} from '../../../../utils/subscription-handler';
import {TtdSegmentPriceDialogComponent, TtdSegmentPriceDialogData} from './ttd-segment-price-dialog.component';

interface PartnerPrice {
  partnerName: string;
  groupName: string;
  price: TtdPriceModel;
}

@Component({
  selector: 'app-ttd-segment-prices',
  template: `
    <mat-card class="price">

      <div class="flex-row">
        <span class="typo-title">Segment Level Prices</span>
        <span class="typo-caption">Price applies to partner, <code>System</code> to all</span>
      </div>

      <mat-card-content>
        <ul>
          <li *ngFor="let partner of partnerPrices | async | orderBy: 'partnerName'" class="prices">
            <span class="amount">
              <span *ngIf="partner.price.type === 'CPM' || partner.price.type === 'Hybrid' ">
                {{ partner.price.cpm | number : '1.2-2' }} USD
              </span>
              <span *ngIf="partner.price.type === 'PercentOfMediaCost'" class="faded">no CPM</span>
            </span>
            <span class="percentage">
              <span *ngIf="partner.price.type !== 'CPM'">
                {{ partner.price.percentOfMediaCostRate | number : '1.0-0' }}%
              </span>
              <span *ngIf="partner.price.type === 'CPM'" class="faded">no %.o.m.</span>
            </span>
            <span class="buyer" *ngIf="partner?.partnerName">{{ partner?.partnerName }} ({{ partner.price.buyerId }}
              )</span>
            <span class="buyer" *ngIf="partner?.groupName">{{ partner?.groupName }} ({{ partner.price.groupId }})</span>
            <span class="status">
              <mat-icon *ngIf="!isDryMode" [ngClass]="getStatusIcon(partner.price.status)"
                        matTooltip="{{partner.price.status}}">
                {{ getStatusIcon(partner.price.status) }}
              </mat-icon>
            <span class="brand"> Brand({{ partner.price.brandId }})</span>
              <button *ngIf="isDryMode" mat-icon-button (click)="removePrice(partner.price)">
                <mat-icon>remove_circle_outline</mat-icon>
              </button>
            </span>
          </li>
        </ul>

        <div *ngIf="partnerPrices !== undefined && (partnerPrices | async)?.length === 0">none</div>

        <div class="add-seat-price-container">
          <small class="message" *ngIf="partnerPrices !== undefined && (partnerPrices | async)?.length === 0">
            <mat-icon>info</mat-icon>
            <span>To change existing price simply add the same partner.</span>
          </small>
          <button class="add-button"
                  mat-raised-button
                  matTooltip="Add Price"
                  color="accent"
                  name="add-ttd-price"
                  data-cy="button-ttd-price-add"
                  (click)="this.addPrice()">
            <mat-icon>add</mat-icon>
            Add Price
          </button>
        </div>

      </mat-card-content>
    </mat-card>

    <div *ngIf="requestError" class="error error-request">
      <span>Failed: {{ requestError?.error["message"] || requestError?.message }}</span>
    </div>
  `,
  styleUrls: ['../account/account.component.scss', 'account-ttd.component.scss']
})
export class TtdSegmentPricesComponent implements OnDestroy {
  @Input() ttdConfig!: TtdConfig[];

  @Input() set prices(value: TtdPriceModel[]) {
    this.currentPrices = value ?? [];
    this.pricesSource.next(this.currentPrices);
  }

  get prices() {
    return this.currentPrices;
  }

  @Input() set partners(value: TtdPartner[]) {
    this.currentPartners = value ?? [];
    this.partnersSource.next(this.currentPartners);
  }

  get partners() {
    return this.currentPartners;
  }

  @Input() isDryMode = false;
  @Input() accountId!: number;
  @Input() segmentId!: number;
  @Input() apiId!: ApiId;
  @Output() pricesChanged = new EventEmitter<TtdPriceModel[]>();

  private subscriptionHandler = new SubscriptionHandler();
  private currentPrices!: TtdPriceModel[];
  private currentPartners!: TtdPartner[];
  private partnerGroups!: TtdGroup[];
  private pricesSource = new ReplaySubject<TtdPriceModel[]>(1);
  private partnersSource = new ReplaySubject<TtdPartner[]>(1);
  private partnerMap = this.partnersSource.pipe(
    map((all) => new Map(all.map(i => [i.partnerId, i])))
  );
  partnerPrices: Observable<PartnerPrice[]> = combineLatest([this.pricesSource, this.partnerMap])
    .pipe(
      map(([prices, partnerMap]) => {
        return prices.map(price => ({
          partnerName: partnerMap.get(price.buyerId as string)?.partnerName,
          groupName: this.partnerGroups?.find(it => it.id === price.groupId)?.name,
          price
        } as PartnerPrice));
      })
    );
  requestError?: HttpErrorResponse;

  constructor(
    private targetService: TargetService,
    private dialog: MatDialog) {
    this.targetService.getTtdGroups().subscribe(it => {
      this.partnerGroups = it;
    });
  }

  ngOnDestroy() {
    this.subscriptionHandler.unsubscribe();
  }

  addPrice() {
    const dialogRef = this.dialog.open<TtdSegmentPriceDialogComponent, TtdSegmentPriceDialogData>(TtdSegmentPriceDialogComponent, {
      data: {
        partners: this.partners,
        currentPrices: this.currentPrices,
        partnerGroups: this.partnerGroups,
        ttdConfig: this.ttdConfig,
        accountApiId: this.apiId
      }
    });

    this.subscriptionHandler.subscription = dialogRef.afterClosed().subscribe((newPrice?: TtdPriceModel) => {
      console.log(newPrice);
      if (!newPrice) {
        return;
      }
      if (this.isDryMode) {
        if (this.prices.find(p => p.buyerId === newPrice.buyerId && p.brandId === newPrice.brandId)) {
          alert(`duplicate prices for buyer #${newPrice.buyerId} and brand #${newPrice.brandId} are not allowed.`);
          return;
        }
        this.prices.push(newPrice);
        this.pricesChanged.emit(this.prices);
        this.pricesSource.next(this.prices);
      } else {
        this.requestError = undefined;
        this.targetService.addTtdPrice(this.accountId, this.segmentId, newPrice)
          .subscribe({
            next: (data: TtdPriceModel) => {
              this.handleAddSuccess(data);
            },
            error: (error: HttpErrorResponse) => {
              this.requestError = error;
            }
          });
      }
    });
  }

  getStatusIcon(status: string) {
    return TargetService.getStatusIcon(status);
  }

  removePrice(price: TtdPriceModel) {

    this.prices = [...this.prices].filter(p => p.buyerId !== price.buyerId && p.brandId !== price.brandId);
    this.pricesChanged.emit(this.prices);
    this.pricesSource.next(this.prices);
    this.pricesChanged.emit(this.prices);

  }

  private handleAddSuccess(data: TtdPriceModel) {
    const newPrices = [...this.prices];

    const priceIndex = newPrices.findIndex((price) => {
      return price.buyerId === data.buyerId && price.brandId === data.brandId;
    });

    if (priceIndex >= 0) {
      newPrices[priceIndex] = data;
    } else {
      newPrices.push(data);
    }

    this.pricesChanged.emit(newPrices);
  }
}
